import {Capacitor} from '@capacitor/core';
import {Device} from '@capacitor/device';
import {Health, HealthDataType, HealthSample} from '@capgo/capacitor-health';
import {Api} from '@/services/ApiService'
import {SchultenMedia} from '@/services/DataService'
import {UserProvider} from '@/features/main.types';

// The only health data Coachio touches, read-only. Steps and distance become habits,
// calories, heart rate and weight become metrics (see HealthConnectRequestHandler in
// the backend). Nothing writes a sample back, so we never ask for write access:
// Play rejected versionCode 35 for requesting more than the features need, and
// WRITE_WEIGHT was the one permission with no feature behind it at all.
const READ_TYPES: HealthDataType[] = ['steps', 'distance', 'calories', 'heartRate', 'weight'];

interface HealthConnectInterface {
  isAvailable(): Promise<boolean>;
  requestAuthorization(callback: (success: boolean, provider: UserProvider) => Promise<void>): Promise<void>;
  checkAuthorization(callback: (success: boolean, provider: UserProvider) => Promise<void>): Promise<void>;
  fetchHealthData(dataType: HealthDataType, startDate?: string, endDate?: string): Promise<HealthSample[]>;
  backgroundFetch(): Promise<void>;
  syncWithBackend(payload: HealthSample[]): Promise<void>;
}

class HealthConnectService implements HealthConnectInterface {
  // Verify that Health Connect (Samsung Health / other providers) is present on the device.
  async isAvailable() {
    if (Capacitor.getPlatform() !== 'android') {
      return false;
    }

    const result = await Health.isAvailable();

    return result.available;
  }

  // Checks authorization status for the provided data types without prompting the user.
  async checkAuthorization(callback: (success: boolean, provider: UserProvider) => Promise<void>) {

    const result = await Health.checkAuthorization({
      read: READ_TYPES,
    });
    console.log('checkAuthorization', result)

    // Unlike HealthKit (which obscures individual read/write grant status), Health
    // Connect reports grants truthfully per type, so a partial grant is a real
    // answer: one read is enough to consider the integration connected.
    if (result.readAuthorized?.length > 0) {
      const deviceId = await Device.getId();

      // If user has Health Connect authorization but no backend integration, register the device
      if (!SchultenMedia.User?.integrations?.health_connect) {
        const registered = await this.registerDevice();
        if (!registered) {
          return;
        }
      }

      await callback(true, {
        user_id: deviceId.identifier,
      });
    }
  }

  // Requests read/write access to the provided data types.
  async requestAuthorization(callback: (success: boolean, provider: UserProvider) => Promise<void>) {
    const result = await Health.requestAuthorization({
      read: READ_TYPES,
    });

    // See checkAuthorization: Health Connect grants per type, so one granted read
    // is enough to register the device.
    if (result.readAuthorized?.length > 0) {
      const [deviceId, deviceInfo] = await Promise.all([
        Device.getId(),
        Device.getInfo(),
      ]);

      try {
        const response = await Api.post(SchultenMedia.API.userProfile.healthConnect.oauth, {
          device_id: deviceId.identifier,
          model: deviceInfo.model,
          platform: deviceInfo.platform,
          operating_system: deviceInfo.operatingSystem,
          os_version: deviceInfo.osVersion,
          manufacturer: deviceInfo.manufacturer,
          is_virtual: deviceInfo.isVirtual,
        });

        await callback(response.success ?? false, {
          user_id: deviceId.identifier,
        });
      } catch (error) {
        console.error("HealthConnectService authentication error:", error)
      }
    }
  }

  // Reads samples for the given data type within the specified time frame.
  async fetchHealthData(dataType: HealthDataType, startDate?: string, endDate?: string) {
    const { samples } = await Health.readSamples({
      dataType,
      startDate,
      endDate,
    });

    return samples;
  }

  // No saveHealthData here on purpose (same as HealthKitService): nothing called it,
  // and keeping it would mean keeping the WRITE_WEIGHT permission it needs. Bringing
  // it back means asking for write access in requestAuthorization AND un-removing
  // the permission in android/app/src/main/AndroidManifest.xml.

  // Data fetching in background mode
  async backgroundFetch() {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    const now = new Date();

    // Get start date from last sync or today's start of day
    const healthConnectIntegration = SchultenMedia.User?.integrations?.health_connect;
    let startDate: Date;

    if (healthConnectIntegration?.last_synced_at) {
        console.log('last sync', healthConnectIntegration.last_synced_at)
      // Use last sync date's start of day
      startDate = new Date(healthConnectIntegration.last_synced_at);
      startDate.setHours(0, 0, 0, 0);
    } else {
      // No last sync - use today's start of day
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
    }

    // Parallel data fetching - all requests run simultaneously
    const payload = (await Promise.all([
      // Using fetchHealthData method for each data type
      this.fetchHealthData('steps', startDate.toISOString(), now.toISOString()), // Daily steps count
      this.fetchHealthData('heartRate', new Date(now.getTime() - 60 * 60 * 1000).toISOString(), now.toISOString()), // Recent heart rate readings
      this.fetchHealthData('distance', startDate.toISOString(), now.toISOString()), // Walking/running distance
      this.fetchHealthData('calories', startDate.toISOString(), now.toISOString()), // Active calories burned
      this.fetchHealthData('weight', startDate.toISOString(), now.toISOString()), // Weight analysis
    ])).flat();

    await this.syncWithBackend(payload);
  }

  // Registers the current device with the backend (OAuth flow)
  private async registerDevice(): Promise<boolean> {
      console.log("HealthConnectService device registration start");
    const [deviceId, deviceInfo] = await Promise.all([
      Device.getId(),
      Device.getInfo(),
    ]);

    try {
      const response = await Api.post(SchultenMedia.API.userProfile.healthConnect.oauth, {
        device_id: deviceId.identifier,
        model: deviceInfo.model,
        platform: deviceInfo.platform,
        operating_system: deviceInfo.operatingSystem,
        os_version: deviceInfo.osVersion,
        manufacturer: deviceInfo.manufacturer,
        is_virtual: deviceInfo.isVirtual,
      });

      console.log("HealthConnectService device registration success");

      // Update local user state with the integration data
      if (response.success && response.data) {
        if (!SchultenMedia.User.integrations) {
          SchultenMedia.User.integrations = {};
        }
        SchultenMedia.User.integrations.health_connect = response.data;
      }

      return response.success ?? false;
    } catch (error) {
      console.error("HealthConnectService device registration error:", error);
      return false;
    }
  }

  // Synchronizes data with the backend.
  async syncWithBackend(payload: HealthSample[]) {
    try {
        const deviceId = await Device.getId();

        const response = await Api.post(SchultenMedia.API.userProfile.healthConnect.webhook, {
            data: payload,
            device_id: deviceId.identifier
        })

        // Update local user state with sync status from webhook response
        if (response.sync_status && SchultenMedia.User?.integrations?.health_connect) {
          SchultenMedia.User.integrations.health_connect.sync_status = response.sync_status;
          SchultenMedia.User.integrations.health_connect.last_synced_at = response.last_synced_at;
        }

        return response;

    } catch (error) {
        console.error("HealthConnectService synchronization error:", error)
    }
  }
}

export default new HealthConnectService();
